import { useEffect, useRef } from "react";

/* ------------------------------------------------------------------ *
 * Compact 3D simplex noise (public-domain, Stefan Gustavson style).
 * Dependency-free so the site stays light + exportable.
 * ------------------------------------------------------------------ */
function makeNoise3D(seed = 1) {
  const p = new Uint8Array(256);
  for (let i = 0; i < 256; i++) p[i] = i;
  // deterministic shuffle
  let s = seed * 1013904223 + 1;
  const rand = () => {
    s = (s * 1664525 + 1013904223) >>> 0;
    return s / 4294967296;
  };
  for (let i = 255; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1));
    const t = p[i];
    p[i] = p[j];
    p[j] = t;
  }
  const perm = new Uint8Array(512);
  const permMod12 = new Uint8Array(512);
  for (let i = 0; i < 512; i++) {
    perm[i] = p[i & 255];
    permMod12[i] = perm[i] % 12;
  }
  const grad3 = new Float32Array([
    1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1, 0, 1, 0, 1, -1, 0, 1, 1, 0, -1, -1, 0,
    -1, 0, 1, 1, 0, -1, 1, 0, 1, -1, 0, -1, -1,
  ]);
  const F3 = 1 / 3;
  const G3 = 1 / 6;

  return function noise3(xin, yin, zin) {
    let n0, n1, n2, n3;
    const s = (xin + yin + zin) * F3;
    const i = Math.floor(xin + s);
    const j = Math.floor(yin + s);
    const k = Math.floor(zin + s);
    const t = (i + j + k) * G3;
    const x0 = xin - (i - t);
    const y0 = yin - (j - t);
    const z0 = zin - (k - t);
    let i1, j1, k1, i2, j2, k2;
    if (x0 >= y0) {
      if (y0 >= z0) { i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 1; k2 = 0; }
      else if (x0 >= z0) { i1 = 1; j1 = 0; k1 = 0; i2 = 1; j2 = 0; k2 = 1; }
      else { i1 = 0; j1 = 0; k1 = 1; i2 = 1; j2 = 0; k2 = 1; }
    } else {
      if (y0 < z0) { i1 = 0; j1 = 0; k1 = 1; i2 = 0; j2 = 1; k2 = 1; }
      else if (x0 < z0) { i1 = 0; j1 = 1; k1 = 0; i2 = 0; j2 = 1; k2 = 1; }
      else { i1 = 0; j1 = 1; k1 = 0; i2 = 1; j2 = 1; k2 = 0; }
    }
    const x1 = x0 - i1 + G3, y1 = y0 - j1 + G3, z1 = z0 - k1 + G3;
    const x2 = x0 - i2 + 2 * G3, y2 = y0 - j2 + 2 * G3, z2 = z0 - k2 + 2 * G3;
    const x3 = x0 - 1 + 3 * G3, y3 = y0 - 1 + 3 * G3, z3 = z0 - 1 + 3 * G3;
    const ii = i & 255, jj = j & 255, kk = k & 255;
    let t0 = 0.6 - x0 * x0 - y0 * y0 - z0 * z0;
    if (t0 < 0) n0 = 0;
    else { const gi0 = permMod12[ii + perm[jj + perm[kk]]] * 3; t0 *= t0; n0 = t0 * t0 * (grad3[gi0] * x0 + grad3[gi0 + 1] * y0 + grad3[gi0 + 2] * z0); }
    let t1 = 0.6 - x1 * x1 - y1 * y1 - z1 * z1;
    if (t1 < 0) n1 = 0;
    else { const gi1 = permMod12[ii + i1 + perm[jj + j1 + perm[kk + k1]]] * 3; t1 *= t1; n1 = t1 * t1 * (grad3[gi1] * x1 + grad3[gi1 + 1] * y1 + grad3[gi1 + 2] * z1); }
    let t2 = 0.6 - x2 * x2 - y2 * y2 - z2 * z2;
    if (t2 < 0) n2 = 0;
    else { const gi2 = permMod12[ii + i2 + perm[jj + j2 + perm[kk + k2]]] * 3; t2 *= t2; n2 = t2 * t2 * (grad3[gi2] * x2 + grad3[gi2 + 1] * y2 + grad3[gi2 + 2] * z2); }
    let t3 = 0.6 - x3 * x3 - y3 * y3 - z3 * z3;
    if (t3 < 0) n3 = 0;
    else { const gi3 = permMod12[ii + 1 + perm[jj + 1 + perm[kk + 1]]] * 3; t3 *= t3; n3 = t3 * t3 * (grad3[gi3] * x3 + grad3[gi3 + 1] * y3 + grad3[gi3 + 2] * z3); }
    return 32 * (n0 + n1 + n2 + n3);
  };
}

// marching-squares segment table (corners tl,tr,br,bl -> edges T,R,B,L)
const CASES = {
  1: [["L", "B"]],
  2: [["B", "R"]],
  3: [["L", "R"]],
  4: [["T", "R"]],
  5: [["T", "R"], ["B", "L"]],
  6: [["T", "B"]],
  7: [["T", "L"]],
  8: [["T", "L"]],
  9: [["T", "B"]],
  10: [["T", "L"], ["B", "R"]],
  11: [["T", "R"]],
  12: [["L", "R"]],
  13: [["B", "R"]],
  14: [["L", "B"]],
};

export default function TopoCanvas({ variant = "paper" }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const noise = makeNoise3D(variant === "light" ? 13 : 7);
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const contained = variant === "light"; // hero stage: size to parent, light lines

    let W = 0, H = 0, dpr = 1;
    let cols = 0, rows = 0, spacing = 34;
    let field = new Float32Array(0);
    const isMobile = window.innerWidth < 768;
    const host = () => (contained ? canvas.parentElement : null);

    // smoothed pointer (elevation bump center) in CSS px
    let px = window.innerWidth * 0.7;
    let py = window.innerHeight * 0.4;
    let sx = px, sy = py;
    let bumpAmp = 0; // eases toward target when pointer is active
    let targetBump = 0;

    const resize = () => {
      const h = host();
      W = h ? h.clientWidth : window.innerWidth;
      H = h ? h.clientHeight : window.innerHeight;
      dpr = Math.min(window.devicePixelRatio || 1, isMobile ? 1 : 1.5);
      spacing = isMobile ? 46 : 34;
      canvas.width = Math.floor(W * dpr);
      canvas.height = Math.floor(H * dpr);
      canvas.style.width = W + "px";
      canvas.style.height = H + "px";
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(W / spacing) + 1;
      rows = Math.ceil(H / spacing) + 1;
      field = new Float32Array((cols + 1) * (rows + 1));
    };
    resize();
    window.addEventListener("resize", resize);
    let ro;
    if (contained && "ResizeObserver" in window) {
      ro = new ResizeObserver(resize);
      if (host()) ro.observe(host());
    }

    const onMove = (e) => {
      if (contained && host()) {
        const r = host().getBoundingClientRect();
        px = e.clientX - r.left;
        py = e.clientY - r.top;
      } else {
        px = e.clientX;
        py = e.clientY;
      }
      targetBump = 1;
    };
    const onLeave = () => { targetBump = 0.15; };
    window.addEventListener("pointermove", onMove, { passive: true });
    window.addEventListener("pointerdown", onMove, { passive: true });
    document.addEventListener("mouseleave", onLeave);

    // contour levels + the single rust "index" band
    const LEVELS = [-0.62, -0.42, -0.22, -0.02, 0.18, 0.38, 0.58];
    const RUST_LEVEL = -0.02;

    const freq = 0.055; // terrain feature scale
    const sigmaCells = (isMobile ? 90 : 150) / spacing; // cursor bump radius in cells
    const inv2Sig2 = 1 / (2 * sigmaCells * sigmaCells);

    let t = 0;
    let raf;
    let frame = 0;

    const edgePoint = (edge, x0, y0, x1, y1, tl, tr, br, bl, L) => {
      switch (edge) {
        case "T": { const d = tr - tl; return [x0 + (d === 0 ? 0.5 : (L - tl) / d) * spacing, y0]; }
        case "R": { const d = br - tr; return [x1, y0 + (d === 0 ? 0.5 : (L - tr) / d) * spacing]; }
        case "B": { const d = br - bl; return [x0 + (d === 0 ? 0.5 : (L - bl) / d) * spacing, y1]; }
        default: { const d = bl - tl; return [x0, y0 + (d === 0 ? 0.5 : (L - tl) / d) * spacing]; }
      }
    };

    const render = () => {
      frame++;
      // ambient background: this is a slow-drifting noise field, so full 60fps
      // isn't perceptible. Throttle recompute to keep the main thread free for
      // scrolling and whatever's actually in view elsewhere on the page.
      const skip = isMobile ? 3 : (contained ? 1 : 2);
      if (skip > 1 && frame % skip !== 0) { raf = requestAnimationFrame(render); return; }

      t += reduced ? 0 : 0.0016;
      const scrollY = window.scrollY || window.pageYOffset || 0;
      const offY = scrollY * 0.0016;

      // ease pointer + bump
      sx += (px - sx) * 0.09;
      sy += (py - sy) * 0.09;
      bumpAmp += (targetBump - bumpAmp) * 0.06;
      const bcx = sx / spacing;
      const bcy = sy / spacing;

      // sample field
      for (let r = 0; r <= rows; r++) {
        for (let c = 0; c <= cols; c++) {
          let v = noise(c * freq, r * freq + offY, t);
          if (bumpAmp > 0.01) {
            const dx = c - bcx, dy = r - bcy;
            v += bumpAmp * 0.95 * Math.exp(-(dx * dx + dy * dy) * inv2Sig2);
          }
          field[r * (cols + 1) + c] = v;
        }
      }

      ctx.clearRect(0, 0, W, H);
      // paper: fade as you leave the hero so text stays clean. light: constant, bold.
      const alphaScale = contained ? 1 : 1 - Math.min(scrollY / 2200, 1) * 0.4;

      for (let li = 0; li < LEVELS.length; li++) {
        const L = LEVELS[li];
        const isRust = L === RUST_LEVEL;
        const isIndex = li % 3 === 0;
        ctx.beginPath();
        for (let r = 0; r < rows; r++) {
          const yb0 = r * spacing;
          const yb1 = yb0 + spacing;
          const rowOff = r * (cols + 1);
          const rowOff1 = (r + 1) * (cols + 1);
          for (let c = 0; c < cols; c++) {
            const tl = field[rowOff + c];
            const tr = field[rowOff + c + 1];
            const br = field[rowOff1 + c + 1];
            const bl = field[rowOff1 + c];
            let m = 0;
            if (tl >= L) m |= 8;
            if (tr >= L) m |= 4;
            if (br >= L) m |= 2;
            if (bl >= L) m |= 1;
            if (m === 0 || m === 15) continue;
            const segs = CASES[m];
            const xb0 = c * spacing;
            const xb1 = xb0 + spacing;
            for (let sI = 0; sI < segs.length; sI++) {
              const [a, b] = segs[sI];
              const pa = edgePoint(a, xb0, yb0, xb1, yb1, tl, tr, br, bl, L);
              const pb = edgePoint(b, xb0, yb0, xb1, yb1, tl, tr, br, bl, L);
              ctx.moveTo(pa[0], pa[1]);
              ctx.lineTo(pb[0], pb[1]);
            }
          }
        }
        if (contained) {
          // dark-on-paper hero stage — bolder than the ambient background variant
          if (isRust) {
            ctx.strokeStyle = `rgba(161,63,44,${(0.5).toFixed(3)})`;
            ctx.lineWidth = 1.4;
          } else {
            const base = isIndex ? 0.22 : 0.1;
            ctx.strokeStyle = `rgba(43,42,38,${base.toFixed(3)})`;
            ctx.lineWidth = isIndex ? 1.15 : 0.85;
          }
        } else if (isRust) {
          ctx.strokeStyle = `rgba(161,63,44,${(0.28 * alphaScale).toFixed(3)})`;
          ctx.lineWidth = 1.3;
        } else {
          const base = isIndex ? 0.11 : 0.055;
          ctx.strokeStyle = `rgba(43,42,38,${(base * alphaScale).toFixed(3)})`;
          ctx.lineWidth = isIndex ? 1.1 : 0.85;
        }
        ctx.stroke();
      }

      raf = requestAnimationFrame(render);
    };

    raf = requestAnimationFrame(render);

    // pause when tab hidden
    const onVis = () => {
      if (document.hidden) cancelAnimationFrame(raf);
      else raf = requestAnimationFrame(render);
    };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", resize);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerdown", onMove);
      document.removeEventListener("mouseleave", onLeave);
      document.removeEventListener("visibilitychange", onVis);
      if (ro) ro.disconnect();
    };
  }, [variant]);

  const contained = variant === "light";

  return (
    <div
      aria-hidden="true"
      className={
        contained
          ? "pointer-events-none absolute inset-0 z-0 overflow-hidden"
          : "pointer-events-none fixed inset-0 z-0"
      }
      data-testid={contained ? "topo-hero" : "topo-background"}
    >
      <canvas
        ref={canvasRef}
        className="h-full w-full"
        style={{
          mixBlendMode: "multiply",
          animation: "topoFadeIn 1.6s ease forwards",
        }}
      />
      <style>{`
        @keyframes topoFadeIn { from { opacity: 0; } to { opacity: 1; } }
        @media (prefers-reduced-motion: reduce) {
          canvas { animation: none !important; }
        }
      `}</style>
    </div>
  );
}
